import { Active } from '@dnd-kit/core';
import React, { useState, createContext, useMemo } from 'react';

import { Account, AccountGroup, AccountProperty, Card } from '../../../../../../../../generated-types';
import useTranslation from '../../../../../../../../hooks/useTranslation';
import { GroupedAccountCardItem, GroupedItems } from '../../../../../../../../models/account';
import { simulateMissingGroups } from '../../../../../../../../services/accountsGroupsService';
import mapAllAccountCardItems from '../../../../mapAccountCardItems';
import { getGroupedAccounts } from './utils';

type ViewMode = 'edit' | 'display';

type DndActiveContext = {
    active: [Active | null, (state: Active | null) => void];
    grouped: [GroupedItems<GroupedAccountCardItem>, (state: GroupedItems<GroupedAccountCardItem>) => void];
    lastSaved: [GroupedItems<GroupedAccountCardItem>, (state: GroupedItems<GroupedAccountCardItem>) => void];
    viewMode: [ViewMode, (state: ViewMode) => void];
    editedHeader: [string | undefined, (state: string | undefined) => void];
    headerValue: [string | undefined, (state: string | undefined) => void];
};

export const DndContext = createContext<DndActiveContext | null>(null);

export const useDndContext = () => {
    const context = React.useContext(DndContext);
    if (context === null) {
        throw new Error('useDndContext must be used within a DndContextProvider');
    }
    return context;
};

type Props = {
    accounts: Account[];
    cards: Card[] | undefined;
    groups: AccountGroup[];
    properties: AccountProperty[];
};

export const DndContextProvider: React.FC<React.PropsWithChildren<Props>> = ({
    accounts,
    cards,
    groups,
    properties,
    children,
}) => {
    const translation = useTranslation();
    const active = useState<Active | null>(null);
    const viewMode = useState<'display' | 'edit'>('display');
    const editedHeader = useState<string>();
    const headerValue = useState<string>();

    const grouped = useState<GroupedItems<GroupedAccountCardItem>>(() =>
        getGroupedAccounts(
            mapAllAccountCardItems(accounts, cards),
            simulateMissingGroups(groups, translation.accountOverview.headingMobile),
            properties,
        ),
    );

    const lastSaved = useState<GroupedItems<GroupedAccountCardItem>>(grouped[0]);

    const value = useMemo(
        () => ({
            active,
            grouped,
            lastSaved,
            viewMode,
            editedHeader,
            headerValue,
        }),
        [active, editedHeader, grouped, headerValue, lastSaved, viewMode],
    );
    return <DndContext.Provider value={value}>{children}</DndContext.Provider>;
};
